import React, { useState, useEffect, useRef, useContext } from 'react';
import { useMutation, useLazyQuery, gql } from '@apollo/client';
import branch from 'branch-sdk';
import ReactGA from 'react-ga4';

import { CONCIERGES, DEFAULT_DOCUMENT_TITLE, DEFAULT_SHARE_URL } from 'utils/Consts';
import { DebugContext } from 'utils/DebugContext';
import { TRIP_FRAGMENT } from 'utils/Fragments';
import { history } from 'utils/History';
import { isGuid } from 'utils/Text';
import { ControlSection, ScheduleSection } from 'sections';

import 'react-loading-skeleton/dist/skeleton.css';
import 'App.css';
import { isDev } from 'utils/Environment';
import { TRACK, recordEvent } from 'utils/Track';

const GENERATE_TRIP = gql`
  mutation($days: Int, $regionId: ID, $travelerId: ID) {
    generateTrip(input: { regionId: $regionId, days: $days, travelerId: $travelerId }) {
      ...TripFragment
    }
  }
  ${TRIP_FRAGMENT}
`;

const TRIP = gql`
  query($tripId: ID!) {
    getTrip(tripId: $tripId) {
      ...TripFragment
    }
  }
  ${TRIP_FRAGMENT}
`;

const MAX_MUTATION_ATTEMPTS = 5; // Max number of times to attempt generating a trip before giving up and showing error message
const ARTIFICIAL_SCHEDULE_DELAY = 1500; // Delay before showing schedule to give time for OpenAI and to build anticipation

const encodePlace = (str) => {
  let encodedStr = encodeURIComponent(str);
  encodedStr = encodedStr.replace(/%20/g, "-");

  return encodedStr;
};

const createBranchUrl = (shareUrl, tripId, isDebug) => {
  const linkData = {
    channel: 'ai_trip_generator',
    feature: 'qr_code',
    data: {
      '$desktop_url': `${shareUrl}/${tripId}`,
      '$canonical_url': `${shareUrl}/${tripId}`,
      generatedTripId: tripId,
    }
  };

  return new Promise((resolve, reject) => {
    branch.link(linkData, (err, link) => {
      if (err) {
        isDebug && console.log('Error creating Branch link:', err);
        reject(err);
      } else {
        isDebug && console.log('Branch link created:', link);
        resolve(link);
      }
    });
  });
};

const normalizeData = (data) => {
  if (data?.generateTrip) {
    data.trip = data.generateTrip;
  } else if (data?.getTrip) {
    data.trip = data.getTrip;
  }
  return data;
};

const App = () => {
  const { isDebug } = useContext(DebugContext);

  const [lastTripError, setLastTripError] = useState(null);
  const [isTripHasError, setIsTripHasError] = useState(false);
  const [isLoadingStarted, setIsLoadingStarted] = useState(false);

  const [doGenerateTrip, { loading: tripMutationLoading, error: tripMutationError }] = useMutation(GENERATE_TRIP, {
    onCompleted: (data) => {
      if (data?.generateTrip?.itinerary && data?.generateTrip?.itinerary?.length > 0) {
        recordEvent(TRACK.EVENTS.GENERATE_TRIP_COMPLETE, {
          tripId: data?.generateTrip?.id,
          regionId: data?.generateTrip?.regions?.[0]?.id,
          days: data?.generateTrip?.itinerary?.length,
          delayBeforeShowing: ARTIFICIAL_SCHEDULE_DELAY,
        });
        isDebug && console.log('generateTrip (onCompleted) data', data);
        showTripUrl(data);
      }
    },
  });

  const [doGetTrip, { loading: tripQueryLoading }] = useLazyQuery(TRIP, {
    onCompleted: (data) => {
      isDebug && console.log('getTrip (onCompleted) data', data);
      if (data?.getTrip?.itinerary && data?.getTrip?.itinerary?.length > 0) {
        recordEvent(TRACK.EVENTS.GET_TRIP_COMPLETE, {
          tripId: data?.getTrip?.id,
          regionId: data?.getTrip?.regions?.[0]?.id,
          days: data?.getTrip?.itinerary?.length,
          delayBeforeShowing: ARTIFICIAL_SCHEDULE_DELAY
        });
        setLastTripError(null);
        setIsTripHasError(false);
        setFetchedTripData(data);
        handleShowSchedule({
          concierge: chosenConcierge,
          isNewSchedule: false,
          isFromUrl: true
        });
      }
    },
    onError: (error) => {
      console.error('getTrip onError', error);
      history.push(`/${isDebug ? '?debug=1' : ''}`);
      document.title(DEFAULT_DOCUMENT_TITLE);
      setIsLoadingStarted(false);
    },
  });

  const doGenerateTripWithRetry = async (variables, attempts = MAX_MUTATION_ATTEMPTS) => {
    if (attempts === 0) {
      recordEvent(TRACK.EVENTS.GENERATE_TRIP_FAILED, {
        regionId: variables?.regionId,
        days: variables?.days,
        conciergeId: variables?.travelerId,
        conciergeName: chosenConcierge?.name,
        attempts: MAX_MUTATION_ATTEMPTS,
      });
      isDebug && console.log(`All ${MAX_MUTATION_ATTEMPTS} attempts failed.`, lastTripError);
      setLastTripError(tripMutationError);
      setIsTripHasError(true);
      setIsLoadingStarted(false);
      setTimeout(() => {
        setIsTripHasError(false);
      }, 0);
      return;
    }
  
    const currentAttempt = MAX_MUTATION_ATTEMPTS - attempts + 1;
  
    isDebug && console.log(`\nStarting attempt ${currentAttempt}...`);
    
    try {
      const response = await doGenerateTrip({ variables });
      const data = response?.data;
      isDebug && console.log('doGenerateTripWithRetry data', data?.generateTrip);
      
      if (data?.generateTrip?.itinerary && data?.generateTrip?.itinerary?.length > 0) {
        isDebug && console.log('%c Trip data generated successfully', 'background: #222; color: #1abc9c');
        setLastTripError(null);
        setIsTripHasError(false);
        setFetchedTripData(data);
        setIsScheduleOpen(true);
        return true;
      } else if (data?.generateTrip?.itinerary && data?.generateTrip?.itinerary?.length === 0) {
        throw new Error('Trip itinerary is empty');
      }
    } catch (error) {
      console.error(`Attempt ${currentAttempt} failed:`, error);
      // If the mutation fails, retry with the same variables and decrement the attempts count
      return doGenerateTripWithRetry(variables, attempts - 1);
    }
  };

  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isFormReset, setIsFormReset] = useState(false);
  const [isLoadingSimulated, setIsLoadingSimulated] = useState(false);
  const [chosenConcierge, setChosenConcierge] = useState(CONCIERGES.classic.name);
  const [fetchedTripData, setFetchedTripData] = useState(null);
  const [generatedShareUrl, setGeneratedShareUrl] = useState(DEFAULT_SHARE_URL);
  const [isScheduleVisibleAfterDelay, setIsScheduleVisibleAfterDelay] = useState(false);

  const isLoadingStateVisible = tripMutationLoading || tripQueryLoading;
  const normalizedTrip = normalizeData(fetchedTripData)?.trip;

  tripMutationLoading && isDebug && console.log('tripMutationLoading', tripMutationLoading);

  const scheduleSectionRef = useRef();
  const isScrollListenerActiveRef = useRef(false);

  const showTripUrl = (data, isAddToHistoryStack = true) => {
    const { trip } = normalizeData(data);
    if (trip?.itinerary && trip?.itinerary?.length > 0) {
      const regionString = encodePlace(trip?.regions?.[0]?.name);
      const tripLength = trip?.itinerary?.length || 0;
      const tripId = trip?.id;
      const tripUrl = `/${tripLength}-Day-${regionString}-Itinerary/${tripId}${isDebug ? '?debug=1' : ''}`;
      isAddToHistoryStack
        ? history.push(tripUrl)
        : history.replace(tripUrl);
    }
  };

  const showDocumentTitle = (data = { trip: {} }) => {
    const { trip } = normalizeData(data);
    if (trip?.itinerary && trip?.itinerary?.length > 0) {
      const regionString = trip?.regions?.[0]?.name;
      const tripLength = trip?.itinerary?.length || 0;
      const tripTitle = `${tripLength}-Day ${regionString} Itinerary | ${DEFAULT_DOCUMENT_TITLE}`;
      document.title = tripTitle;
    } else {
      document.title = DEFAULT_DOCUMENT_TITLE;
    }
  };

  const simulateLoading = () => {
    setIsLoadingSimulated(true);
    setTimeout(() => {
      setIsLoadingSimulated(false);
    }, 300);
  };

  const handleHideSchedule = () => {
    simulateLoading();
    setIsFormReset(true);
    history.replace(`/${isDebug ? '?debug=1' : ''}`);
    document.title = DEFAULT_DOCUMENT_TITLE;
    setIsScheduleOpen(false);
    setTimeout(() => {
      setIsFormReset(false);
    }, 0);
  };

  const handleShowSchedule = ({
    concierge = CONCIERGES.classic.name,
    isNewSchedule = true,
    isFromUrl = false
  }) => {
    simulateLoading();
    setIsFormReset(true);
    setChosenConcierge(concierge);
    setIsScheduleOpen(true);
    !isFromUrl && showTripUrl(fetchedTripData, isNewSchedule);
    setIsLoadingStarted(false);
    setTimeout(() => {
      setIsFormReset(false);
      showDocumentTitle(fetchedTripData);
    }, 0);
  };

  const handleGenerateTrip = async (variables) => {
    isDebug && console.log('generateTrip variables', variables);
    setIsTripHasError(false);
    setIsLoadingStarted(true);
    setFetchedTripData(null);
    setIsScheduleVisibleAfterDelay(false);
    await doGenerateTripWithRetry(variables);
    if (lastTripError) {
      console.error('LastError:', lastTripError);
    }
  };

  // Allow scrolling the open schedule section from the control section
  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
      scheduleSectionRef.current.scrollTop += e.deltaY;
    };

    const contentLeft = document.getElementById('content-left');

    if (isScheduleOpen && !isScrollListenerActiveRef.current) {
      contentLeft.addEventListener('wheel', handleWheel);
      isScrollListenerActiveRef.current = true;
    } else if (!isScheduleOpen && isScrollListenerActiveRef.current) {
      contentLeft.removeEventListener('wheel', handleWheel);
      isScrollListenerActiveRef.current = false;
    }

    return () => {
      contentLeft.removeEventListener('wheel', handleWheel);
      isScrollListenerActiveRef.current = false;
    };
  }, [isScheduleOpen]);
  

  useEffect(() => {
    normalizedTrip && isDebug && console.log('Trip in schedule (right section)', normalizedTrip);
    if (normalizedTrip?.itinerary?.length > 0) {
      isDebug && console.log(`%c Schedule ready and will be visible in ${ARTIFICIAL_SCHEDULE_DELAY}ms`, 'background: #222; color: #1abc9c');
      // Start preloading hero image before showing the schedule
      const heroImage = new Image();
      heroImage.src = normalizedTrip?.regions[0]?.heroImageUrl || normalizedTrip?.photos?.[0];
      const timer = setTimeout(() => {
        isDebug && console.log('%c Schedule is now visible', 'background: #222; color: #1abc9c');
        setIsScheduleVisibleAfterDelay(true);
      }, ARTIFICIAL_SCHEDULE_DELAY);
  
      return () => {
        clearTimeout(timer);
      };
    } else {
      setIsScheduleVisibleAfterDelay(false);
    }
  }, [normalizedTrip]);

  useEffect(() => {
    const generateShareUrl = async () => {
      if (normalizedTrip?.id && chosenConcierge) {
        const branchUrl = await createBranchUrl(`https://${isDev ? 'qa.' : ''}takemeoutofoffice.com/trips/${chosenConcierge}`, normalizedTrip?.id, isDebug);
        setGeneratedShareUrl(branchUrl);
      } else {
        setGeneratedShareUrl(DEFAULT_SHARE_URL);
      }
    };
  
    generateShareUrl();
  }, [normalizedTrip, chosenConcierge]);

  useEffect(() => {
    if (isLoadingStateVisible) {
      document.title = `Preparing... | ${DEFAULT_DOCUMENT_TITLE}`;
    }
  }, [isLoadingStateVisible]);

  useEffect(() => {
    isDebug && console.log('%c OOO. AI Trip Generator - v0.19b ', 'background: #222; color: #bada55');
    isDebug && console.log(`Connected to ${isDev ? 'QA' : 'Prod'} environment`);
    // Prevents issue on mobile where the page height is larger than the viewport
    const setAppHeight = () => {
      const doc = document.documentElement;
      doc.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    ReactGA.send({ hitType: "pageview", page: `${window.location.pathname}${window.location.search}` });
    setAppHeight();
    window.addEventListener('resize', setAppHeight);
    // If the trip ID is in the URL, fetch the trip data on page load
    const path = window.location.pathname;
    const pathParts = path.split('/');
    pathParts[0] && pathParts[1] && isDebug && console.log('pathParts', pathParts);
    const tripId = pathParts[pathParts.length - 1];
    if (tripId?.length > 0 && isGuid(tripId)) {
      isDebug && console.log('%c Trip ID found in URL', 'background: #222; color: #1abc9c');
      setChosenConcierge(CONCIERGES.classic.name);
      recordEvent(TRACK.EVENTS.GET_TRIP, {
        tripId,
      });
      setIsLoadingStarted(true);
      setFetchedTripData(null);
      setIsScheduleVisibleAfterDelay(false);
      doGetTrip({ variables: { tripId } });
    } else {
      history.replace(`/`);
    }
    return () => {
      window.removeEventListener('resize', setAppHeight);
    }
  }, []);

  return (
    <div className={`content-container ${isScheduleOpen || isLoadingStateVisible || isLoadingSimulated ? 'lock-mobile-vertical-scroll' : ''}`}>
      <div className={`content ${isScheduleOpen && isScheduleVisibleAfterDelay ? 'schedule-open' : ''}`}>
        <ControlSection
          onShowSchedule={handleShowSchedule}
          onHideSchedule={handleHideSchedule}
          onGenerateTrip={handleGenerateTrip}
          isTripLoading={(isLoadingStateVisible || !isScheduleVisibleAfterDelay) && isLoadingStarted}
          isLoadingSimulated={isLoadingSimulated}
          isTripHasError={isTripHasError}
          tripData={normalizeData(fetchedTripData)}
          isFormReset={isFormReset}
          shareUrl={generatedShareUrl}
        />
        {!isLoadingStarted && normalizedTrip?.itinerary?.length > 0 ? (
          <ScheduleSection
            ref={scheduleSectionRef}
            onHideSchedule={handleHideSchedule}
            onSetDocumentTitle={showDocumentTitle}
            trip={normalizedTrip}
            shareUrl={generatedShareUrl}
          />
        ) : null}
      </div>
    </div>
  );
};

export default App;
